/**
 * NewForm design-system Badge.
 *
 * Pill-shaped status / category indicator. Renders the framework UI kit's
 * `.b` block 1:1 — Rethink Sans 12px / weight 500 / leading-none, 22px
 * height, rounded-full, sentence-case labels (not uppercase). The design
 * system reserves uppercase mono for kickers/eyebrows/table headers; pills
 * stay sans + sentence case.
 *
 *   import { Badge } from "@/components/ui/design-system/badge";
 *
 *   <Badge variant="ok">Active</Badge>
 *   <Badge variant="ok" dot>Top performer</Badge>
 *   <Badge variant="brand">New</Badge>
 *   <Badge variant="grad">Pro plan</Badge>
 *
 * Variants (matching the spec preview at
 * packages/nf-design-system/preview/components-badges.html):
 *
 *   default — dark navy background, white text (status: neutral primary)
 *   secondary — muted gray background, dark text (status: neutral default)
 *   outline — white background, hairline border (status: archived / neutral)
 *   ok — green tint (status: success, top performer)
 *   warn — amber tint (status: pending, capped, throttled)
 *   err — red tint (status: failed, error)
 *   brand — solid NewForm purple (status: featured, new)
 *   brand-soft — purple tint (status: role/category — admin, staff)
 *   grad — purple→magenta gradient (status: premium / Pro plan)
 *
 * For icons, pass them as children alongside the label — the consumer
 * controls icon sizing. Use `dot` for the simple status-dot indicator that
 * inherits the variant's text color.
 */

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full font-nf-ds-sans text-xs font-medium leading-none whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        outline: "bg-background border border-border text-foreground",
        ok: "bg-green-100 text-green-900",
        warn: "bg-amber-100 text-amber-900",
        err: "bg-red-100 text-red-900",
        info: "bg-blue-100 text-blue-900",
        idle: "bg-muted text-muted-foreground",
        brand: "bg-nf-ds-purple-product text-nf-ds-white",
        "brand-soft":
          "bg-nf-ds-purple-product/[0.08] text-nf-ds-purple-product",
        grad: "bg-gradient-to-r from-nf-ds-purple-product to-nf-ds-magenta text-nf-ds-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Render a small status dot (6px) before the children, in the variant's
   * text color. Use for "status with indicator" pills like "● Top performer".
   */
  dot?: boolean;
}

export function Badge({
  className,
  variant,
  dot,
  children,
  ...props
}: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}

export { badgeVariants };
